import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { getNavigationForRoute } from "@/features/Navigation/utils/getNavigationForRoute";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { StorePackageModal } from "@/screens/Store/components/StorePackageModal";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  CheckIcon,
  ChevronMiniRight,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";

import { StoreStackNavigatorParamList } from "../../StoreStackNavigator";
import { PackageItemActions_package$key } from "./__generated__/PackageItemActions_package.graphql";
import { PackageItemActions_session$key } from "./__generated__/PackageItemActions_session.graphql";

type Props = {
  packageRef?: PackageItemActions_package$key | null;
  sessionRef?: PackageItemActions_session$key | null;
  customerHasPackage: boolean;
  openPackageModalOnLoad?: boolean;
  onPackageAuthComplete?: () => void;
};
export const PackageItemActions = ({
  packageRef,
  sessionRef,
  customerHasPackage,
  openPackageModalOnLoad,
  onPackageAuthComplete,
}: Props) => {
  const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = useState(false);

  const storeNavigation =
    useNavigation<NavigationProp<StoreStackNavigatorParamList>>();

  const session = useFragment(
    PackageItemActionsSessionFragmentString,
    sessionRef
  );
  const pkg = useFragment(PackageItemActionsFragmentString, packageRef);
  const isMembership = pkg?.__typename === "MembershipPackage";

  useEffect(() => {
    setIsLearnMoreModalOpen(openPackageModalOnLoad ?? false);
  }, [openPackageModalOnLoad]);

  const onLearnMorePress = () => {
    if (Platform.OS === "web") {
      setIsLearnMoreModalOpen(true);
    } else {
      if (pkg?.id) {
        storeNavigation.navigate("ProductDetail", { productId: pkg.id });
      }
    }
  };

  const onManagePress = () => {
    getNavigationForRoute<ClubStackNavigatorParamList>(
      storeNavigation,
      "Home"
    ).navigate("Home", {
      screen: "Profile",
      params: {
        screen: "Memberships",
        params: { screen: "MembershipsList" },
      },
    });
  };

  return (
    <>
      {customerHasPackage && isMembership ? (
        <HStack justifyContent="space-between" alignItems="center">
          <Button
            accessibilityLabel={t`Manage`}
            size="tiny"
            variant="outline"
            rightIcon={<ChevronMiniRight />}
            onPress={onManagePress}
          >{t`Manage`}</Button>
          <HStack
            backgroundColor="surface-main-primary-subtlest"
            px={2}
            height={32}
            borderRadius="2"
            alignItems="center"
            gap={2}
          >
            <Text variant="mainTiny" color="text-success">{t`Active`}</Text>
            <CheckIcon color="text-success" />
          </HStack>
        </HStack>
      ) : (
        <HStack>
          <Button
            accessibilityLabel={t`Learn More`}
            size="tiny"
            rightIcon={<ChevronMiniRight />}
            onPress={onLearnMorePress}
          >{t`Learn More`}</Button>
        </HStack>
      )}
      {isLearnMoreModalOpen ? (
        <StorePackageModal
          packageRef={pkg}
          sessionRef={session}
          isOpen={isLearnMoreModalOpen}
          onClose={() => setIsLearnMoreModalOpen(false)}
          onAuthComplete={onPackageAuthComplete}
        />
      ) : null}
    </>
  );
};

const PackageItemActionsFragmentString = graphql`
  fragment PackageItemActions_package on Package {
    id
    __typename
    ...StorePackageModal_package
  }
`;

const PackageItemActionsSessionFragmentString = graphql`
  fragment PackageItemActions_session on Session {
    ...StorePackageModal_session
  }
`;
